import React, { useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";

import Model from "@tripian/model";
import { FormTemplateRegister } from "@tripian/react";

import * as gtag from "../../gtag";
import {
  REGISTER,
  LOGIN,
  USE_TRIP_URL,
  NOT_FOUND,
  TRIPS,
  TRIP_CLONE,
  VISA_AUTH_REDIRECT,
} from "../../constants/ROUTER_PATH_TITLE";
import { saveLocalStorageToken } from "../../App/AppWrapper/localStorages";

import useUser from "../../hooks/useUser";
import useAuth from "../../hooks/useAuth";
import useTranslate from "../../hooks/useTranslate";
import { useLocalStorage } from "../../hooks/useLocalStorage";
import { useGuestUser } from "../../hooks/useGuestUser";

import { Navbar } from "../../components/Navbar/Navbar";
import VisaSteps from "../../components/VisaSteps/VisaSteps";

import tripianLogo from "../../assets/img/Tripian_Logo.png";
import classes from "./Register.module.scss";
import ValaiRegister from "../ValaiLanding/ValaiRegister";

const DefaultRegisterPage = () => {
  const { isLoggedIn, loggedIn, registerEmail } = useAuth();
  const { user, userFetch } = useUser();
  const { t } = useTranslate();
  const isGuest = useGuestUser();

  const useQuery = <T extends { [K in keyof T]?: string } = {}>(): T => {
    const query = new URLSearchParams(useLocation().search);
    const result: T = {} as T;
    query.forEach((val, key) => {
      result[key] = val;
    });

    return result;
  };
  const { sharedTripHash } = useQuery<{ sharedTripHash: string | undefined }>();

  const { getLocalData } = useLocalStorage<"light" | "dark">("theme");
  const theme = getLocalData();

  const navigate = useNavigate();
  const location = useLocation();

  document.title = REGISTER.TITLE(t("user.register"));

  useEffect(() => {
    if (isGuest && user?.userType === -1) {
      return;
    }

    // Already logged in. Redirect to where came from or home
    if (isLoggedIn) {
      const from: any = location.state || { from: { pathname: TRIPS.PATH } };

      const isVisaFlow =
        window.tconfig.ELIGIBILITY_TYPE === "visa" &&
        user?.userType !== Model.USER_TYPE.ELIGIBILITY;

      // If there is visa flow, redirect to VisaAuthRedirect page
      if (isVisaFlow) {
        const queryParams = sharedTripHash
            ? `?sharedTripHash=${sharedTripHash}`
            : "";
        navigate(`${VISA_AUTH_REDIRECT.PATH}${queryParams}`, {
          replace: true,
          state: { visaSource: "register" },
        });
        return;
      }

      if (sharedTripHash) {
        navigate(`${TRIP_CLONE.PATH}/${sharedTripHash}`, { replace: true });
      } else {
        navigate(from.from, { replace: true });
      }

      // HIDE LOGIN for butter redirect to hash login
    } else if (!window.tconfig.SHOW_LOGIN) {
      navigate(USE_TRIP_URL.PATH, { replace: true });

      // Register disabled, redirect to not found
    } else if (!window.tconfig.SHOW_REGISTER)
      navigate(NOT_FOUND.PATH, { replace: true });
    else {
      // Do nothing
    }
  }, [isLoggedIn, navigate, location, sharedTripHash, user?.userType, isGuest]);

  const callbackSuccessRegister = (userEmail: string, token: Model.Token) => {
    gtag.googleAnalyticsEvent("Created new user", { user: userEmail });
    saveLocalStorageToken(token);

    loggedIn();
    userFetch(true);
  };

  const isVisa = window.tconfig.ELIGIBILITY_TYPE === "visa";

  return (
    <div
      className={`${classes.registerPage} ${isVisa ? classes.visaAuth : ""}`}
    >
      <Navbar theme={theme} t={t} />
      <div className="full-center-x pb12">
        <FormTemplateRegister
          register={registerEmail}
          successRegister={callbackSuccessRegister}
          reCaptchaSiteKey={window.tconfig.RECAPTCHA_SITE_KEY}
          showCaptcha={process.env.NODE_ENV === "production"}
          goBackLogin={() => navigate(LOGIN.PATH)}
          showRegister={window.tconfig.SHOW_REGISTER}
          registerFields={window.tconfig.REGISTER_FIELDS}
          headerContent={isVisa ? <VisaSteps activeStep={1} t={t} /> : undefined}
          subtitle={isVisa ? t("visa.auth.register.eligibilityLine") : undefined}
          footerNote={isVisa ? t("visa.auth.register.nextNote") : undefined}
          title={
            isVisa
              ? t("landing.howTripianWorks.createYourAccount.title")
              : undefined
          }
          submitText={isVisa ? t("create_account") : undefined}
          socialLoginConfigs={{
            clientId: window.tconfig.COGNITO.CLIENT_ID,
            domain: window.tconfig.COGNITO.DOMAIN,
            identityProviders: window.tconfig.COGNITO.IDENTITY_PROVIDERS,
            region: window.tconfig.COGNITO.REGION,
            baseDomain: `${window.tconfig.DOMAIN_ORIGIN}${window.tconfig.DOMAIN_ROUTER_BASE_NAME}`,
          }}
          t={t}
        />

        <a
          className={classes.registerPoweredBy}
          target="_blank"
          rel="noreferrer"
          href="https://www.tripian.com/"
        >
          <span>Powered by</span>
          <img src={tripianLogo} alt="" />
        </a>
      </div>

      {window.tconfig.IMAGE_PATHS.FORM_BG_IMG_URL && (
        <img
          className="login-app-background hide-s"
          src={window.tconfig.IMAGE_PATHS.FORM_BG_IMG_URL}
          alt={window.tconfig.BRAND_NAME}
        />
      )}
    </div>
  );
};

// Wrapper component to conditionally render ValaiRegister or DefaultRegisterPage
const RegisterPage = () => {
  if (window.tconfig.LANDING_PAGE === "ValaiLanding") {
    return <ValaiRegister />;
  }
  return <DefaultRegisterPage />;
};

export default RegisterPage;
